import React, { useState } from 'react';
import { Row, Col, Card, Typography, Button, Result, message, Modal, Tag, Divider } from 'antd';
import { 
  CheckCircleOutlined, 
  CalendarOutlined, 
  ClockCircleOutlined, 
  UserOutlined, 
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  SwapOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { PageContainer } from './styled';
import BookingService from '../../services/BookingService';
import moment from 'moment';

const { Text, Paragraph, Title } = Typography;

// Helper function to format duration (accepts hours from backend)
const formatDuration = (hours) => {
  if (!hours) return '';
  const wholeHours = Math.floor(hours);
  const remainingMinutes = Math.round((hours - wholeHours) * 60);
  if (wholeHours >= 1 && remainingMinutes === 0) {
    return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'}`;
  }
  if (wholeHours >= 1) {
    return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'} ${remainingMinutes} min`;
  }
  return `${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`;
};

// Get status color and text
const getStatusDisplay = (status) => {
  const statusMap = {
    confirmed: { color: 'green', text: 'Confirmed', icon: <CheckCircleOutlined /> },
    pending: { color: 'orange', text: 'Pending', icon: <ClockCircleOutlined /> },
    cancelled: { color: 'red', text: 'Cancelled', icon: <CloseCircleOutlined /> },
    completed: { color: 'blue', text: 'Completed', icon: <CheckCircleOutlined /> },
    'no-show': { color: 'default', text: 'No Show', icon: <CloseCircleOutlined /> }
  };
  return statusMap[status] || { color: 'default', text: status, icon: null };
};

const BookingLookup = ({ booking, onNewBooking, onBookingCancelled }) => {
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelled, setIsCancelled] = useState(booking?.status === 'cancelled');

  if (!booking) {
    return (
      <PageContainer>
        <div style={{ padding: '40px 0' }}>
          <Result
            status="error"
            title="Booking Not Found"
            subTitle="We couldn't find a booking with that confirmation code."
            extra={[
              <Button type="primary" key="new" onClick={onNewBooking}>
                Book New Appointment
              </Button>
            ]}
          />
        </div>
      </PageContainer>
    );
  }

  const handleCancelBooking = async () => {
    setCancelLoading(true);
    try {
      const response = await BookingService.cancelBooking(booking.confirmationCode);
      if (response.success) {
        message.success('Booking cancelled successfully');
        setIsCancelled(true);
        setShowCancelModal(false);
        if (onBookingCancelled) {
          // Small delay to show the cancelled state before navigating
          setTimeout(() => onBookingCancelled(), 1500);
        }
      } else {
        message.error(response.error || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      message.error('Failed to cancel booking. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReschedule = () => {
    Modal.info({
      title: 'Reschedule Your Appointment',
      content: (
        <div>
          <p>To reschedule your appointment:</p>
          <ol style={{ paddingLeft: '20px', marginTop: '12px' }}>
            <li>Contact us at <strong>Tara@blackheartassociation.org</strong></li>
            <li>Provide your confirmation code: <Text code>{booking.confirmationCode}</Text></li>
            <li>Let us know your preferred new date and time</li>
          </ol>
          <p style={{ marginTop: '16px', color: '#666' }}>
            We&apos;ll get back to you within 24 hours to confirm your new appointment.
          </p>
        </div>
      ),
      okText: 'Got it',
      width: 480
    });
  };

  const generateCalendarEvent = () => {
    const startDate = moment(booking.timeSlot?.startTime).format('YYYYMMDDTHHmmss');
    const endDate = moment(booking.timeSlot?.endTime).format('YYYYMMDDTHHmmss');
    const eventName = booking.eventType?.name || booking.eventLocation?.eventName || 'BHA Appointment';
    
    const calendarUrl = `data:text/calendar;charset=utf8,BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Black Heart Association//Booking System//EN
BEGIN:VEVENT
UID:${booking.confirmationCode}@blackheartassociation.org
DTSTAMP:${moment().format('YYYYMMDDTHHmmss')}Z
DTSTART:${startDate}Z
DTEND:${endDate}Z
SUMMARY:${eventName} - Black Heart Association
DESCRIPTION:Health screening appointment with Black Heart Association
LOCATION:${booking.eventLocation?.address || 'Location TBD'}
END:VEVENT
END:VCALENDAR`;

    const link = document.createElement('a');
    link.href = calendarUrl;
    link.download = `BHA-${booking.confirmationCode}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const statusDisplay = getStatusDisplay(isCancelled ? 'cancelled' : booking.status);
  const isPastAppointment = moment(booking.timeSlot?.startTime).isBefore(moment());
  const canModify = !isCancelled && !isPastAppointment && booking.status !== 'completed';

  // Show cancelled state
  if (isCancelled) {
    return (
      <PageContainer>
        <div style={{ padding: '40px 0' }}>
          <Result
            status="success"
            title="Booking Cancelled"
            subTitle={
              <div>
                Your appointment has been cancelled successfully.
                <br />
                Confirmation code <Text code>{booking.confirmationCode}</Text> is no longer active.
              </div>
            }
            extra={[
              <Button type="primary" key="new" onClick={onNewBooking}>
                Book New Appointment
              </Button>
            ]}
          />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div style={{ padding: '40px 0' }} className="booking-lookup-wrapper">
        <style>
          {`
            @media (max-width: 768px) {
              .booking-lookup-wrapper {
                padding: 24px 0 !important;
              }
              .booking-lookup-wrapper .ant-card {
                border-radius: 8px !important;
              }
              .booking-lookup-wrapper .ant-card-head {
                padding: 12px 16px !important;
              }
              .booking-lookup-wrapper .ant-card-body {
                padding: 16px !important;
              }
            }
            @media (max-width: 480px) {
              .booking-lookup-wrapper {
                padding: 16px 0 !important;
              }
              .booking-lookup-wrapper h3 {
                font-size: 20px !important;
              }
              .booking-lookup-wrapper .ant-btn {
                width: 100% !important;
                margin-bottom: 8px !important;
              }
            }
          `}
        </style>
        {/* Header Section */}
        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} lg={16}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <img 
                src="/bha-original-logo.png" 
                alt="Black Heart Association" 
                style={{ height: '50px', marginBottom: '16px' }}
              />
              <Title level={3} style={{ margin: '0 0 8px 0' }}>
                Your Booking Details
              </Title>
              <div style={{ marginBottom: '16px' }}>
                <Tag color={statusDisplay.color} icon={statusDisplay.icon} style={{ fontSize: '14px', padding: '4px 12px' }}>
                  {statusDisplay.text}
                </Tag>
              </div>
            </div>
          </Col>
        </Row>

        {/* Confirmation Code Card */}
        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} lg={16}>
            <Card style={{ marginBottom: '24px', textAlign: 'center' }} className="confirmation-code-card">
              <style>
                {`
                  @media (max-width: 480px) {
                    .confirmation-code-card {
                      padding: 16px !important;
                    }
                    .confirmation-code-card .code-display {
                      font-size: 24px !important;
                      letter-spacing: 1px !important;
                    }
                  }
                `}
              </style>
              <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Confirmation Code
              </Text>
              <div className="code-display" style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff', letterSpacing: '2px', margin: '8px 0' }}>
                {booking.confirmationCode}
              </div>
              <Text type="secondary">
                Save this code to manage your booking
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Appointment Details */}
        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} lg={16}>
            <Card title="Appointment Details" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Service
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>
                      {booking.eventType?.name || booking.eventLocation?.eventName || 'N/A'}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Date
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>
                      {booking.timeSlot?.startTime 
                        ? moment(booking.timeSlot.startTime).format('dddd, MMMM D, YYYY')
                        : 'N/A'}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Time
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>
                      {booking.timeSlot?.startTime && booking.timeSlot?.endTime
                        ? `${moment(booking.timeSlot.startTime).format('h:mm A')} - ${moment(booking.timeSlot.endTime).format('h:mm A')} CST`
                        : 'N/A'}
                    </div>
                  </div>

                  {booking.eventType?.duration && (
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        Duration
                      </Text>
                      <div style={{ paddingLeft: '24px' }}>
                        {formatDuration(booking.eventType.duration)}
                      </div>
                    </div>
                  )}
                </Col>

                <Col xs={24} sm={12}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <UserOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Name
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>
                      {booking.userInfo?.firstName} {booking.userInfo?.lastName}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <MailOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Email
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>{booking.userInfo?.email}</div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <PhoneOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      Phone
                    </Text>
                    <div style={{ paddingLeft: '24px' }}>{booking.userInfo?.phone || 'N/A'}</div>
                  </div>

                  {booking.eventLocation?.address && (
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <EnvironmentOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        Location
                      </Text>
                      <div style={{ paddingLeft: '24px' }}>
                        {booking.eventLocation.address}
                        {booking.eventLocation.city && `, ${booking.eventLocation.city}`}
                        {booking.eventLocation.state && `, ${booking.eventLocation.state}`}
                        {booking.eventLocation.zipCode && ` ${booking.eventLocation.zipCode}`}
                      </div>
                    </div>
                  )}
                </Col>
              </Row>

              <Divider />

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }} className="action-buttons">
                <style>
                  {`
                    @media (max-width: 480px) {
                      .action-buttons {
                        flex-direction: column !important;
                      }
                      .action-buttons .ant-btn {
                        width: 100% !important;
                      }
                    }
                  `}
                </style>
                {canModify && (
                  <Button 
                    type="primary" 
                    icon={<CalendarOutlined />}
                    onClick={generateCalendarEvent}
                  >
                    Add to Calendar
                  </Button>
                )}
                <Button onClick={onNewBooking}>
                  Book Another Appointment
                </Button>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Modify Booking Card - Only show for active future bookings */}
        {canModify && (
          <Row gutter={[24, 24]} justify="center">
            <Col xs={24} lg={16}>
            <Card title="Need to Change Your Appointment?" className="modify-booking-card">
              <style>
                {`
                  @media (max-width: 480px) {
                    .modify-booking-card .ant-row {
                      gap: 12px !important;
                    }
                    .modify-booking-card .ant-btn {
                      width: 100% !important;
                    }
                  }
                `}
              </style>
              <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <div style={{ 
                      padding: '20px', 
                      background: '#f0f5ff', 
                      borderRadius: '8px',
                      border: '1px solid #adc6ff',
                      height: '100%'
                    }}>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#1890ff' }}>
                        <SwapOutlined style={{ marginRight: '8px' }} />
                        Reschedule Appointment
                      </Text>
                      <Paragraph style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
                        Need a different time? Let us know and we&apos;ll help you find a new slot.
                      </Paragraph>
                      <Button 
                        type="primary" 
                        ghost
                        onClick={handleReschedule}
                        icon={<SwapOutlined />}
                      >
                        Reschedule
                      </Button>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ 
                      padding: '20px', 
                      background: '#fff1f0', 
                      borderRadius: '8px',
                      border: '1px solid #ffa39e',
                      height: '100%'
                    }}>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#ff4d4f' }}>
                        <StopOutlined style={{ marginRight: '8px' }} />
                        Cancel Appointment
                      </Text>
                      <Paragraph style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
                        Can&apos;t make it? Cancel your appointment to free up the slot for others.
                      </Paragraph>
                      <Button 
                        danger
                        onClick={() => setShowCancelModal(true)}
                        icon={<StopOutlined />}
                      >
                        Cancel Booking
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        )}

        {/* Past appointment notice */}
        {isPastAppointment && !isCancelled && (
          <Row gutter={[24, 24]} justify="center" style={{ marginTop: '24px' }}>
            <Col xs={24} lg={16}>
              <Card style={{ background: '#f5f5f5', textAlign: 'center' }}>
                <Text type="secondary">
                  This appointment has already passed. Need to book a new appointment?
                </Text>
                <div style={{ marginTop: '12px' }}>
                  <Button type="primary" onClick={onNewBooking}>
                    Book New Appointment
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal
        title={
          <span style={{ color: '#ff4d4f' }}>
            <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
            Cancel Booking
          </span>
        }
        open={showCancelModal}
        onCancel={() => setShowCancelModal(false)}
        footer={[
          <Button key="back" onClick={() => setShowCancelModal(false)}>
            Keep My Booking
          </Button>,
          <Button 
            key="cancel" 
            danger 
            loading={cancelLoading}
            onClick={handleCancelBooking}
          >
            Yes, Cancel Booking
          </Button>
        ]}
      >
        <div style={{ padding: '16px 0' }}>
          <Paragraph>
            Are you sure you want to cancel this appointment?
          </Paragraph>
          <div style={{ 
            background: '#f5f5f5', 
            padding: '16px', 
            borderRadius: '8px',
            marginBottom: '16px' 
          }}>
            <Text strong>Confirmation Code:</Text> <Text code>{booking.confirmationCode}</Text>
            <br />
            <Text strong>Date:</Text> {moment(booking.timeSlot?.startTime).format('dddd, MMMM D, YYYY')}
            <br />
            <Text strong>Time:</Text> {moment(booking.timeSlot?.startTime).format('h:mm A')}
          </div>
          <Paragraph type="warning" style={{ marginBottom: 0 }}>
            This action cannot be undone. You&apos;ll need to book a new appointment if you change your mind.
          </Paragraph>
        </div>
      </Modal>
    </PageContainer>
  );
};

export default BookingLookup;
